import React, { useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Avatar,
} from '@mui/material';
import { Order, OrderItems } from '../../../store/order/api/interface';
import { getAssetURl, resolveAssetUrl } from '../../../utils/url';
import { IsNotNullOrEmpty } from '../../../utils/valid';
import { formatPlainPrice } from '../../../utils/formatNumber';
import { useAppSelector } from '../../../store';
import {
  getCakeDisplayUnitPrice,
  getCakeRowDisplayTotal,
  getCakeUnitPrice,
  getOrderCakeInstructionForRow,
} from '../../../utils/orderTotals';
import {
  buildColorLabelByCode,
  getColorDisplayLabel,
} from '../../../utils/colorDisplay';

interface IProps {
  orderItem: OrderItems;
  key: string;
  idx: number;
  order: Order;
  handleOpenLightBox: (imageObject: any) => void;
  mergedImage?: string | null;
}

const formatBreakdownPrice = (amount: number) =>
  amount > 0 ? formatPlainPrice(amount) : '-';

const pickCatalogPrice = (
  price?: string | number | null,
  discountedPrice?: string | number | null
) => {
  const discounted = Number(discountedPrice) || 0;
  if (discounted > 0) return discounted;
  return Number(price) || 0;
};

const TableRowViewOrderDetails: React.FC<IProps> = ({
  orderItem,
  idx,
  order,
  handleOpenLightBox,
  mergedImage,
}) => {
  const [open, setOpen] = useState(false);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const { data: paletteColors } = useAppSelector((state) => state.color);
  const colorLabelByCode = useMemo(
    () => buildColorLabelByCode(paletteColors),
    [paletteColors]
  );
  const colorCode = orderItem?.order_cake?.colorCode;
  const colorDisplayLabel = getColorDisplayLabel(colorCode, colorLabelByCode, '-');

  // Parse image data from printImageUrl (may contain JSON with both images)
  const parseImageData = (printImageUrl: string | null | undefined) => {
    if (!printImageUrl) return { printImageUrl: null, customImageUrl: null };
    
    try {
      // Try to parse as JSON (new format with both images)
      const parsed = JSON.parse(printImageUrl);
      return {
        printImageUrl: parsed.printImageUrl || null,
        customImageUrl: parsed.customImageUrl || null,
      };
    } catch {
      // If not JSON, treat as old format (only printImageUrl)
      return {
        printImageUrl: printImageUrl,
        customImageUrl: null,
      };
    }
  };

  const cake: any = orderItem?.order_cake;
  const imageData = parseImageData(cake?.printImageUrl);
  const shapePrice =
    cake?.shapePrice != null && Number(cake?.shapePrice) > 0
      ? Number(cake.shapePrice)
      : pickCatalogPrice(
          cake?.shape?.price,
          cake?.shape?.discountedPrice
        );
  const flavorPrice =
    cake?.flavorPrice != null && Number(cake?.flavorPrice) >= 0
      ? Number(cake.flavorPrice)
      : (Number(cake?.flavor?.pricePerKg) || 0) * (Number(cake?.shape?.kg) || 0);
  const topping = cake?.topping_category_item;
  const toppingPrice =
    cake?.toppingPrice != null && Number(cake?.toppingPrice) >= 0
      ? Number(cake.toppingPrice)
      : topping && topping.code !== 'without'
      ? pickCatalogPrice(topping.price, topping.discountedPrice)
      : 0;
  const colorPrice = 0;
  const regionPrintCharge =
    Number(order?.userDetails?.region?.printingCharges) || 0;
  const storedCakePrice = Number(orderItem?.unitPrice) || 0;
  const explainedWithoutPrint = shapePrice + flavorPrice + toppingPrice;
  const printRemainder = Math.max(
    0,
    Number((storedCakePrice - explainedWithoutPrint).toFixed(2))
  );
  const printPrice =
    cake?.printPrice != null && Number(cake?.printPrice) >= 0
      ? Number(cake.printPrice)
      : imageData.printImageUrl
      ? regionPrintCharge > 0
        ? regionPrintCharge
        : printRemainder
      : 0;

  const rowTotal = getCakeRowDisplayTotal(
    orderItem?.qty,
    orderItem?.unitPrice,
    orderItem?.order_cake
  );
  const rowUnitPrice = getCakeDisplayUnitPrice(
    orderItem?.unitPrice,
    orderItem?.order_cake
  );

  return (
    <React.Fragment>
      <>
          <TableRow
            key={idx}
            sx={{
              borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
            }}
          >
            <TableCell align='left'>
              {IsNotNullOrEmpty(orderItem?.order_cake) && (
                <IconButton
                  aria-label='expand row'
                  size='small'
                  onClick={() => setOpen(!open)}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              )}
            </TableCell>

            <TableCell>{idx + 1}</TableCell>

            <TableCell align='left'>
              {orderItem?.addon_product?.previewUrl && (
                <Avatar
                  onClick={() =>
                    handleOpenLightBox(orderItem?.addon_product?.previewUrl)
                  }
                  imgProps={{ crossOrigin: 'anonymous' }}
                  src={getAssetURl(
                    orderItem?.addon_product?.previewUrl,
                    assetUrl
                  )}
                  sx={{
                    borderRadius: 'unset',
                    m: 1,
                    objectFit: 'contain',
                    '& img': { objectFit: 'contain' },
                    cursor: 'pointer',
                  }}
                />
              )}
              {mergedImage && (
                <Avatar
                  onClick={() => handleOpenLightBox(mergedImage)}
                  imgProps={{ crossOrigin: 'anonymous' }}
                  src={mergedImage}
                  sx={{
                    borderRadius: 'unset',
                    m: 1,
                    objectFit: 'contain',
                    '& img': { objectFit: 'contain' },
                    cursor: 'pointer',
                  }}
                />
              )}
            </TableCell>
            <TableCell align='right'>
              {(() => {
                const imageData = parseImageData(orderItem?.order_cake?.printImageUrl);
                return imageData.customImageUrl ? (
                  <Avatar
                    onClick={() => handleOpenLightBox(imageData.customImageUrl)}
                    imgProps={{ crossOrigin: 'anonymous' }}
                    src={resolveAssetUrl(imageData.customImageUrl, assetUrl)}
                    sx={{
                      borderRadius: 'unset',
                      m: 1,
                      objectFit: 'contain',
                      '& img': { objectFit: 'contain' },
                      cursor: 'pointer',
                    }}
                  />
                ) : (
                  '-'
                );
              })()}
            </TableCell>
            <TableCell align='right'>
              {(() => {
                const imageData = parseImageData(orderItem?.order_cake?.printImageUrl);
                return imageData.printImageUrl ? (
                <Avatar
                    onClick={() => handleOpenLightBox(imageData.printImageUrl)}
                  imgProps={{ crossOrigin: 'anonymous' }}
                    src={resolveAssetUrl(imageData.printImageUrl, assetUrl)}
                  sx={{
                    borderRadius: 'unset',
                    m: 1,
                    objectFit: 'contain',
                    '& img': { objectFit: 'contain' },
                    cursor: 'pointer',
                  }}
                />
              ) : (
                '-'
                );
              })()}
            </TableCell>
            <TableCell align='right'>
              {orderItem?.order_cake
                ? `${orderItem?.order_cake?.shapeLabelEn} (${orderItem?.order_cake?.flavorLabelEn})`
                : '-'}
            </TableCell>
            <TableCell align='right'>
              {orderItem?.addon_product
                ? `${orderItem?.addon_product?.labelEn} `
                : '-'}
            </TableCell>
            <TableCell align='right'>
              {getOrderCakeInstructionForRow(orderItem)}
            </TableCell>
            <TableCell align='right'>
              {orderItem?.order_cake
                ? orderItem?.order_cake?.writeOnCake || '-'
                : '-'}
            </TableCell>
            <TableCell align='right'>{orderItem?.qty}</TableCell>
            <TableCell align='right'>{rowUnitPrice}</TableCell>
            <TableCell align='right'>{rowTotal}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              style={{ paddingBottom: 0, paddingTop: 0 }}
              colSpan={12}
            >
              <Collapse
                in={open}
                timeout='auto'
                unmountOnExit
              >
                <Box>
                  <Table
                    size='small'
                    aria-label='purchases'
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell align='center'>{'Type'}</TableCell>
                        <TableCell align='center'>{'Name'}</TableCell>
                        <TableCell align='center'>{'Image'}</TableCell>
                        <TableCell align='center'>{'Price'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderItem?.order_cake && (
                        <>
                      <TableRow key={orderItem?.order_cake?.flavorId}>
                        <TableCell align='center'>Flavor</TableCell>
                        <TableCell align='center'>
                          {orderItem?.order_cake?.flavor?.labelEn} (
                          {orderItem?.order_cake?.flavor?.labelAr})
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {orderItem?.order_cake?.flavor?.previewUrl && (
                            <Avatar
                              onClick={() =>
                                handleOpenLightBox(
                                  orderItem?.order_cake?.flavor?.previewUrl
                                )
                              }
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={getAssetURl(
                                orderItem?.order_cake?.flavor?.previewUrl,
                                assetUrl
                              )}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                                cursor: 'pointer',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align='center'>{formatBreakdownPrice(flavorPrice)}</TableCell>
                      </TableRow>
                      <TableRow key={orderItem?.order_cake?.shapeId}>
                        <TableCell align='center'>Shape</TableCell>
                        <TableCell align='center'>
                          {orderItem?.order_cake?.shape?.labelEn} (
                          {orderItem?.order_cake?.shape?.labelAr})
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {orderItem?.order_cake?.shape?.previewUrl && (
                            <Avatar
                              onClick={() =>
                                handleOpenLightBox(
                                  orderItem?.order_cake?.shape?.previewUrl
                                )
                              }
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={getAssetURl(
                                orderItem?.order_cake?.shape?.previewUrl,
                                assetUrl
                              )}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                                cursor: 'pointer',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align='center'>{formatBreakdownPrice(shapePrice)}</TableCell>
                      </TableRow>
                      {orderItem?.order_cake?.toppingId && (
                        <TableRow key={orderItem?.order_cake?.toppingId}>
                          <TableCell align='center'>Toppings</TableCell>
                          <TableCell align='center'>
                            {
                              orderItem?.order_cake?.topping_category_item
                                ?.labelEn
                            }{' '}
                            (
                            {
                              orderItem?.order_cake?.topping_category_item
                                ?.labelAr
                            }
                            ){' '}
                            {orderItem?.order_cake?.topping_category_item
                              ?.deliveryDayGap === 0
                              ? '(Fast Delivery)'
                              : ''}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{ display: 'flex', justifyContent: 'center' }}
                          >
                            {orderItem?.order_cake?.topping_category_item
                              ?.previewUrl && (
                              <Avatar
                                onClick={() =>
                                  handleOpenLightBox(
                                    orderItem?.order_cake?.topping_category_item
                                      ?.previewUrl
                                  )
                                }
                                imgProps={{ crossOrigin: 'anonymous' }}
                                src={getAssetURl(
                                  orderItem?.order_cake?.topping_category_item
                                    ?.previewUrl,
                                  assetUrl
                                )}
                                sx={{
                                  borderRadius: 'unset',
                                  m: 1,
                                  objectFit: 'contain',
                                  '& img': { objectFit: 'contain' },
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align='center'>{formatBreakdownPrice(toppingPrice)}</TableCell>
                        </TableRow>
                      )}
                      <TableRow key={`color-${orderItem?.order_cake?.colorCode}`}>
                        <TableCell align='center'>Color</TableCell>
                        <TableCell align='center'>
                          {colorDisplayLabel}
                        </TableCell>
                        <TableCell align='center'>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <div
                              style={{
                                width: 18,
                                height: 18,
                                backgroundColor: colorCode || '#ffffff',
                                marginLeft: 8,
                                borderRadius: '50%',
                              }}
                            />
                          </div>
                        </TableCell>
                        <TableCell align='center'>{formatBreakdownPrice(colorPrice)}</TableCell>
                      </TableRow>
                      {imageData.printImageUrl && (
                        <TableRow>
                          <TableCell align='center'>Print Image</TableCell>
                          <TableCell align='center'>Photo printing</TableCell>
                          <TableCell
                            align='center'
                            sx={{ display: 'flex', justifyContent: 'center' }}
                          >
                            <Avatar
                              onClick={() =>
                                handleOpenLightBox(imageData.printImageUrl)
                              }
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={resolveAssetUrl(imageData.printImageUrl, assetUrl)}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                                cursor: 'pointer',
                              }}
                            />
                          </TableCell>
                          <TableCell align='center'>{formatBreakdownPrice(printPrice)}</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell align='center'>Premium Packaging</TableCell>
                        <TableCell align='center'>
                          {orderItem?.order_cake?.cakeBox?.labelEn ||
                            (orderItem?.order_cake as any)?.cake_box?.labelEn ||
                            '-'}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {(() => {
                            const cakeBoxImgUrl =
                              orderItem?.order_cake?.cakeBox?.imgUrl ||
                              (orderItem?.order_cake as any)?.cake_box?.imgUrl;
                            if (!cakeBoxImgUrl) return '-';
                            const cakeBoxSrc = getAssetURl(
                              cakeBoxImgUrl,
                              assetUrl
                            );
                            return (
                              <Avatar
                                variant='rounded'
                                onClick={() =>
                                  handleOpenLightBox(cakeBoxImgUrl)
                                }
                                src={cakeBoxSrc}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  m: 1,
                                  cursor: 'pointer',
                                  '& img': { objectFit: 'contain' },
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell align='center'>
                          {cake?.cakeBoxPrice != null && Number(cake?.cakeBoxPrice) > 0
                            ? formatPlainPrice(Number(cake.cakeBoxPrice))
                            : orderItem?.order_cake?.cakeBox ||
                              (orderItem?.order_cake as any)?.cake_box
                            ? formatPlainPrice(
                                (
                                  orderItem?.order_cake?.cakeBox ||
                                  (orderItem?.order_cake as any)?.cake_box
                                )?.price ?? 0
                              )
                            : '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align='center'>Cake</TableCell>
                        <TableCell align='center'>
                          {orderItem?.order_cake?.shapeLabelEn} (
                          {orderItem?.order_cake?.flavorLabelEn})
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {mergedImage ? (
                            <Avatar
                              onClick={() => handleOpenLightBox(mergedImage)}
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={mergedImage}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                                cursor: 'pointer',
                              }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {getCakeUnitPrice(orderItem?.unitPrice)}
                        </TableCell>
                      </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        </>
    </React.Fragment>
  );
};

export default TableRowViewOrderDetails;
